import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import { useTranslation } from 'react-i18next';
import Input from '@material-ui/core/Input';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import ContactInfo from '../ContactInfo/ContactInfo';
import styles from './SMSChannelInfo.module.css';
import { useRootStore } from '../../../../mobx/bridge';

const useStyles = makeStyles({
  underline: {
    '&:before': {
      borderBottom: '2px solid #00892e',
    },
    '&:after': {
      borderBottom: '1px solid #00892e',
    },
  },
});

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const SMSChannelInfo = observer(function SMSChannelInfo() {
  const { settingsStore, sendingEvents, commonStore } = useRootStore();

  const settings = settingsStore.getLoggedUserSettings;
  const contact = settingsStore.getCustomerInSelectedChannel;

  const [firstName, setFirstName] = React.useState(contact?.FirstName || '');
  const [lastName, setLastName] = React.useState(contact?.LastName || '');

  const { t } = useTranslation();

  const classes = useStyles();

  React.useEffect(() => {
    if (settings?.SelectedChannel?.CustomerPhoneNumber?.Number) {
      sendingEvents.socketEmitMatchContact(settings.SelectedChannel.CustomerPhoneNumber.Number);
    }
  }, [settings.SelectedChannel, sendingEvents]);

  const setOriginalFirstAndLastName = React.useCallback(() => {
    setFirstName(contact?.FirstName || '');
    setLastName(contact?.LastName || '');
  }, [contact?.FirstName, contact?.LastName]);

  const toggleDisplayEditCustomerNames = () => {
    setOriginalFirstAndLastName();
    commonStore.setToggleDisplayEditCustomerNamesForm();
  };

  const updateFirstName = (e) => {
    setFirstName(e.target.value);
  };

  const updateLastName = (e) => {
    setLastName(e.target.value);
  };

  const onSubmit = (e) => {
    e.preventDefault();

    sendingEvents.socketEmitEditCustomerNames({
      contactId: contact._id,
      firstName: firstName?.trim(),
      lastName: lastName?.trim(),
    });
    setOriginalFirstAndLastName();
    commonStore.setToggleDisplayEditCustomerNamesForm();
  };

  const onCancel = () => {
    commonStore.setToggleDisplayEditCustomerNamesForm();
  };

  const contactNotInPOSList = React.useMemo(
    () =>
      contact &&
      commonStore.customersData?.length > 0 &&
      !commonStore.customersData.some((c) =>
        c.phones?.some((p) =>
          contact.PhoneNumbers?.some(
            (contactPhone) => `+1${p.number}` === contactPhone.TwilioFormat
          )
        )
      ),
    [commonStore?.customersData, contact]
  );

  return (
    <React.Fragment>
      <Grid component="div" item xs={12} align="center" className={styles.innerContainer}>
        <div className={styles.channelIcon} />

        <div className={styles.channelDescription}>{t('SMSChannelInfo.TextChat')}</div>

        <div className={styles.channelName}>
          <div className={styles.customerNameContainer}>
            {contact?.getFullName || t('Common.UnknownContact')}
          </div>

          {contactNotInPOSList ? (
            <div className={styles.editCustomerNamesEditBtnContainer}>
              {!commonStore.displayEditCustomerNamesForm ? (
                <React.Fragment>
                  <button
                    type="button"
                    onClick={toggleDisplayEditCustomerNames}
                    className={styles.editCustomerNamesBtn}
                  >
                    {t('Common.Edit')}
                  </button>
                  <Tooltip
                    arrow
                    enterDelay={1000}
                    title={t('SMSChannelInfo.EditCustomerNamesInfoMessage')}
                  >
                    <div className={styles.helpIcon} />
                  </Tooltip>
                </React.Fragment>
              ) : null}
            </div>
          ) : null}
        </div>

        {settings?.SelectedChannel?.CustomerPhoneNumber?.Number ? (
          <div className={styles.primaryNumber}>
            {settings?.SelectedChannel?.CustomerPhoneNumber?.Number}
          </div>
        ) : null}

        {commonStore.displayEditCustomerNamesForm ? (
          <div className={styles.editCustomerNamesContainer}>
            <div>
              <form onSubmit={onSubmit}>
                <div className={styles.editCustomerNamesInputsContainer}>
                  <Input
                    type="text"
                    value={firstName}
                    onChange={updateFirstName}
                    placeholder={t('SMSChannelInfo.FirstNameInputPlaceholder')}
                    className={styles.editCustomerNamesInput}
                    classes={{ underline: classes.underline }}
                  />
                  <Input
                    type="text"
                    value={lastName}
                    onChange={updateLastName}
                    placeholder={t('SMSChannelInfo.LastNameInputPlaceholder')}
                    className={styles.editCustomerNamesInput}
                    classes={{ underline: classes.underline }}
                  />
                </div>
                <div>
                  <button type="submit" className={styles.editCustomerNamesBtn}>
                    {t('Common.Save')}
                  </button>
                  <button type="button" className={styles.editCustomerNamesBtn} onClick={onCancel}>
                    {t('Common.Cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
      </Grid>

      <Grid component="div" item xs={12}>
        <ContactInfo contactToEdit={contact} />
      </Grid>
    </React.Fragment>
  );
});

SMSChannelInfo.whyDidYouRender = true;
SMSChannelInfo.displayName = 'SMSChannelInfo';

export default SMSChannelInfo;
